import { Link } from '@tanstack/react-router';
import { useLocation } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import CardsIcon from '@common/assets/icons/cards.svg?react';
import ChatIcon from '@common/assets/icons/chat.svg?react';
import HeartIcon from '@common/assets/icons/heart.svg?react';
import ListBulletsIcon from '@common/assets/icons/list_bullets.svg?react';
import ProfileIcon from '@common/assets/icons/profile.svg?react';
import { BottomSheetConfig, useTabbar } from '@common/providers/useTabbar';
import { useAppStore } from '@common/stores/useAppStore';
import { Indicator } from '@common/ui/Indicator';

import { useTaskLimits } from '@entities/tasks/hooks/useTaskLimits';

import { analytics } from '@services/AnalyticsService';

import styles from './Tabbar.module.scss';

export interface TabbarProps {
  showNavigation?: boolean;
  showBottomSheet?: boolean;
  bottomSheet?: React.ReactNode;
  config?: BottomSheetConfig;
}

export const Tabbar = ({
  showNavigation = true,
  showBottomSheet = true,
  bottomSheet,
  config,
}: TabbarProps) => {
  const location = useLocation();
  const { setBottomSheet, setShowNavigation } = useTabbar();

  const [initialPage, setInitialPage] = useState(location.pathname);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      setTimeout(() => {
        document.documentElement.style.setProperty(
          '--tabbar-height',
          `${ref.current?.clientHeight}px`,
        );
      }, 250);
    }
  }, [bottomSheet, showBottomSheet, showNavigation]);

  useEffect(() => {
    if (location.pathname === initialPage) {
      return;
    }

    setInitialPage(location.pathname);

    setBottomSheet(undefined);
    setShowNavigation(true);
  }, [initialPage, location.pathname, setBottomSheet, setShowNavigation]);

  return (
    <div className={styles.tabbar} data-test-id="tabbar-root" ref={ref}>
      <AnimatePresence>
        {showBottomSheet && bottomSheet && (
          <motion.div
            data-test-id="tabbar-bottom-sheet"
            style={{
              position: config?.isAbsolute ? 'absolute' : 'relative',
              zIndex: 1,
            }}
            initial={{ y: '100%' }}
            animate={{ y: bottomSheet ? 0 : '100%' }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.2 }}
          >
            {bottomSheet}
          </motion.div>
        )}
      </AnimatePresence>

      {showNavigation && <TabbarNavigation />}
    </div>
  );
};

function TabbarNavigation() {
  // Get counts from the unified store
  const { likesCount, unreadChatsCount, tasks } = useAppStore();
  const { limitReached, getAvailableCountWithinLimit } = useTaskLimits();

  const [, setProfileClickCount] = useState(0);

  const handleProfileClick = () => {
    setProfileClickCount((prev) => {
      const newCount = prev + 1;
      if (newCount === 10) {
        //@ts-expect-error - eruda is a global debug library
        eruda.init();
        return 0;
      }
      return newCount;
    });
  };

  const resetProfileClickCount = () => {
    setProfileClickCount(0);
  };

  const tasksCount = useMemo(
    () => (limitReached ? 0 : getAvailableCountWithinLimit(tasks)),
    [tasks, limitReached, getAvailableCountWithinLimit],
  );

  return (
    <ul
      className={styles.buttons}
      data-test-id="tabbar-navigation"
      style={{ position: 'relative', zIndex: 2 }}
    >
      <li className={styles.button}>
        <Link
          data-test-id="tabbar-dating-button"
          to="/dating"
          onClick={resetProfileClickCount}
        >
          <Indicator>
            <CardsIcon className={styles.icon} />
          </Indicator>
          <span>Знакомства</span>
        </Link>
      </li>
      <li className={styles.button}>
        <Link
          data-test-id="tabbar-likes-button"
          to="/likes"
          onClick={() => {
            resetProfileClickCount();
            analytics.trackLikesOpened();
          }}
        >
          <Indicator value={likesCount}>
            <HeartIcon className={styles.icon} />
          </Indicator>
          <span>Лайки</span>
        </Link>
      </li>
      <li className={styles.button}>
        <Link
          data-test-id="tabbar-chats-button"
          to="/chats"
          onClick={() => {
            resetProfileClickCount();
            analytics.trackChatOpened();
          }}
        >
          <Indicator value={unreadChatsCount}>
            <ChatIcon className={styles.icon} />
          </Indicator>
          <span>Чаты</span>
        </Link>
      </li>
      <li className={styles.button}>
        <Link
          data-test-id="tabbar-tasks-button"
          to="/tasks"
          onClick={() => {
            resetProfileClickCount();
            analytics.trackTasksOpened();
          }}
        >
          <Indicator value={tasksCount}>
            <ListBulletsIcon className={styles.icon} />
          </Indicator>
          <span>Задания</span>
        </Link>
      </li>
      <li className={styles.button}>
        <Link
          data-test-id="tabbar-profile-button"
          to="/profile"
          resetScroll
          onClick={handleProfileClick}
        >
          <Indicator>
            <ProfileIcon className={styles.icon} />
          </Indicator>
          <span>Профиль</span>
        </Link>
      </li>
    </ul>
  );
}
